import { useEffect, useState } from "react";
import {
  fetchConferenceByHost,
  fetchCountries,
  fetchFocusedSessionAvailability as apiFetchFocusedSessionAvailability,
  fetchFocusedSessions as apiFetchFocusedSessions,
  fetchGenderOptions as apiFetchGenderOptions,
  fetchMasterDetails,
} from "../conferenceRegistrationApi";

const DEFAULT_COUNTRIES = [
  "India",
  "United States",
  "United Kingdom",
  "Australia",
  "Canada",
].map((name) => ({
  label: name,
  value: name,
}));

export const useRegistrationLookups = ({
  conferenceApi,
  currentHost,
  setFormData,
}) => {
  const [bannerUrl, setBannerUrl] = useState(null);
  const [categories, setCategories] = useState([]);
  const [registrationTypes, setRegistrationTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [conferenceId, setConferenceId] = useState(null);
  const [conferenceName, setConferenceName] = useState(null);
  const [focusedSessions, setFocusedSessions] = useState([]);
  const [focusedSessionAvailability, setFocusedSessionAvailability] = useState({});
  const [genderOptions, setGenderOptions] = useState([]);

  const fetchFocusedSessionAvailability = async (sessionId) => {
    if (!sessionId) {
      return null;
    }

    try {
      return await apiFetchFocusedSessionAvailability({
        conferenceApi,
        sessionId,
      });
    } catch (error) {
      console.error(
        `Error fetching focused session availability for ${sessionId}:`,
        error
      );
      return null;
    }
  };

  const loadFocusedSessionAvailability = async (sessionsArray) => {
    const availabilityResults = await Promise.all(
      sessionsArray.map(async (session) => {
        try {
          const availability = await fetchFocusedSessionAvailability(session.id);
          return availability ? [session.id, availability] : null;
        } catch (availabilityError) {
          console.error(
            `Error fetching availability for focused session ${session.id}:`,
            availabilityError
          );
          return null;
        }
      })
    );

    return availabilityResults.reduce((acc, entry) => {
      if (entry) {
        const [sessionId, availability] = entry;
        acc[sessionId] = availability;
      }
      return acc;
    }, {});
  };

  const fetchFocusedSessions = async () => {
    if (!conferenceId) {
      setFocusedSessions([]);
      setFocusedSessionAvailability({});
      return;
    }

    try {
      const sessionsArray = await apiFetchFocusedSessions({
        conferenceApi,
        conferenceId,
      });
      setFocusedSessions(sessionsArray);

      const availabilityMap = await loadFocusedSessionAvailability(sessionsArray);
      setFocusedSessionAvailability(availabilityMap);
    } catch (error) {
      console.error("Error fetching focused sessions:", error);
      setFocusedSessions([]);
      setFocusedSessionAvailability({});
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const data = await fetchConferenceByHost({
          conferenceApi,
          host: currentHost,
        });

        if (data.bannerUrl) setBannerUrl(data.bannerUrl);
        if (data.prefix) {
          setFormData((currentFormData) => ({
            ...currentFormData,
            prefix: data.prefix,
          }));
        }

        if (data.name) {
          setConferenceName(data.name);
          setFormData((currentFormData) => ({
            ...currentFormData,
            conference: data.name,
          }));
        }

        const confId = data.id;
        if (!confId) {
          return;
        }

        setConferenceId(confId);

        fetchMasterDetails({
          conferenceApi,
          conferenceId: confId,
          type: "category",
        })
          .then(setCategories)
          .catch(() => setCategories([]));

        fetchMasterDetails({
          conferenceApi,
          conferenceId: confId,
          type: "conferenceType",
        })
          .then(setRegistrationTypes)
          .catch(() => setRegistrationTypes([]));

        fetchMasterDetails({
          conferenceApi,
          conferenceId: confId,
          type: "Department",
        })
          .then(setDepartments)
          .catch(() => setDepartments([]));

        fetchMasterDetails({
          conferenceApi,
          conferenceId: confId,
          type: "FocussedSessions",
        })
          .then(async (sessionsArray) => {
            setFocusedSessions(sessionsArray);
            const availabilityMap =
              await loadFocusedSessionAvailability(sessionsArray);
            setFocusedSessionAvailability(availabilityMap);
          })
          .catch(() => {
            setFocusedSessions([]);
            setFocusedSessionAvailability({});
          });
      } catch (error) {
        console.error("Failed to fetch conference data:", error);
      }
    };

    const loadCountries = async () => {
      try {
        const list = await fetchCountries();
        setCountries(list.length ? list : DEFAULT_COUNTRIES);
      } catch {
        setCountries(DEFAULT_COUNTRIES);
      }
    };

    loadInitialData();
    loadCountries();
  }, [conferenceApi, currentHost, setFormData]);

  useEffect(() => {
    const loadGenderOptions = async () => {
      if (!conferenceId) {
        setGenderOptions([]);
        return;
      }

      try {
        const genderArray = await apiFetchGenderOptions({
          conferenceApi,
          conferenceId,
        });
        setGenderOptions(genderArray);
      } catch (error) {
        console.error("Error fetching gender options:", error);
        setGenderOptions([]);
      }
    };

    loadGenderOptions();
  }, [conferenceApi, conferenceId]);

  return {
    bannerUrl,
    categories,
    registrationTypes,
    departments,
    countries,
    conferenceId,
    conferenceName,
    focusedSessions,
    setFocusedSessions,
    focusedSessionAvailability,
    setFocusedSessionAvailability,
    genderOptions,
    fetchFocusedSessions,
  };
};
